import { Component } from '@theme/component';

/**
 * Series index on the shop-all catalogue. Once the strip is stuck under the header it names the
 * series in view (aria-current), keeps that link inside the phone strip's visible area, and paints
 * its ground so rows can pass beneath it. Before it sticks it stays transparent, so it never sits as
 * a slab over the page's top glow and never claims a chapter the reader has not reached.
 *
 * Keyed on section ids, never on order: the merchant can arrange the series in any order and
 * series numbers need not be consecutive.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement[]} links
 * @property {HTMLElement} strip
 *
 * @extends {Component<Refs>}
 */
class DtCatalogIndexComponent extends Component {
  requiredRefs = ['links', 'strip'];

  /** @type {HTMLElement | undefined} */
  #sentinel;
  /** @type {IntersectionObserver | undefined} */
  #stickObserver;
  /** @type {IntersectionObserver | undefined} */
  #spyObserver;
  #visible = new Set();
  #stuck = false;
  /** @type {HTMLAnchorElement | null} */
  #current = null;

  connectedCallback() {
    super.connectedCallback();
    this.dataset.ready = '';

    // Stuck: a zero-height marker just above the strip has scrolled up past the header.
    const header = Math.ceil(parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0);
    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    this.before(this.#sentinel);
    this.#stickObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#stuck = !entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
        this.#render();
      },
      { rootMargin: `-${header + 1}px 0px 0px 0px` }
    );
    this.#stickObserver.observe(this.#sentinel);

    // In view: the series overlapping a band from just under the strip down to 45% of the viewport.
    this.#spyObserver = new IntersectionObserver(this.#onSpy, { rootMargin: '-120px 0px -55% 0px' });
    for (const link of this.refs.links) {
      const section = document.getElementById(link.hash.slice(1));
      if (section) this.#spyObserver.observe(section);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickObserver?.disconnect();
    this.#spyObserver?.disconnect();
    this.#sentinel?.remove();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onSpy = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#visible.add(entry.target.id);
      else this.#visible.delete(entry.target.id);
    }
    // Between two chapters no section is in the band: keep the last one.
    const next = this.refs.links.find((link) => this.#visible.has(link.hash.slice(1)));
    if (next) this.#current = next;
    this.#render();
  };

  #render() {
    this.toggleAttribute('data-stuck', this.#stuck);
    const current = this.#stuck ? this.#current : null;
    for (const link of this.refs.links) {
      if (link === current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
    if (!current) return;

    const { strip } = this.refs;
    const left = current.offsetLeft;
    const right = left + current.offsetWidth;
    if (left < strip.scrollLeft || right > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: left - 16, behavior: 'instant' });
    }
  }
}

if (!customElements.get('dt-catalog-index-component')) {
  customElements.define('dt-catalog-index-component', DtCatalogIndexComponent);
}
